// resources/js/src/store/commentStore.ts
import { create } from "zustand";
import {
  commentApi,
  ApiComment,
  CreateCommentPayload,
} from "../services/comment.api";
import { ApiError } from "../services/api";
import { ReactionType } from "../components/community/components/post/interfaces/types";
import { useAuthStore } from "../auth/auth.store";

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function parseError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Network error";
}

/**
 * أضف رد في الشجرة recursively (nested).
 */
function addReplyToTree(
  comments: ApiComment[],
  parentId: number,
  reply: ApiComment
): ApiComment[] {
  return comments.map((c) =>
    c.id === parentId
      ? { ...c, replies: [...c.replies, reply] }
      : { ...c, replies: addReplyToTree(c.replies, parentId, reply) }
  );
}

/**
 * عدّل كومنت في الشجرة recursively.
 */
function updateInTree(
  comments: ApiComment[],
  id: number,
  patch: Partial<ApiComment>
): ApiComment[] {
  return comments.map((c) =>
    c.id === id
      ? { ...c, ...patch }
      : { ...c, replies: updateInTree(c.replies, id, patch) }
  );
}

/**
 * امسح كومنت من الشجرة recursively.
 */
function removeFromTree(comments: ApiComment[], id: number): ApiComment[] {
  return comments
    .filter((c) => c.id !== id)
    .map((c) => ({ ...c, replies: removeFromTree(c.replies, id) }));
}

/**
 * اعكس ترتيب الكومنتات (الأحدث الأول).
 * Backend بيرجع asc، فبنعكسهم عشان يظهروا desc.
 */
function reverseComments(comments: ApiComment[]): ApiComment[] {
  return comments
    .map((c) => ({
      ...c,
      replies: c.replies ? reverseComments(c.replies) : [],
    }))
    .reverse();
}

/*
|--------------------------------------------------------------------------
| Store Shape
|--------------------------------------------------------------------------
*/

type CommentStoreState = {
  /** key = postId (نستخدم string عشان zustand object keys) */
  byPost: Record<string, ApiComment[]>;

  /** loading flags per post */
  isLoadingByPost: Record<string, boolean>;
  isSubmittingByPost: Record<string, boolean>;
  errorByPost: Record<string, string>;

  /** hasMore per post */
  hasMoreByPost: Record<string, boolean>;
  nextCursorByPost: Record<string, string | null>;

  // Actions
  loadComments: (postId: number | string, reset?: boolean) => Promise<void>;
  loadMore: (postId: number | string) => Promise<void>;

  addComment: (
    postId: number | string,
    payload: CreateCommentPayload
  ) => Promise<boolean>;

  editComment: (
    postId: number | string,
    commentId: number,
    body: string
  ) => Promise<boolean>;

  deleteComment: (
    postId: number | string,
    commentId: number
  ) => Promise<boolean>;

  reactToComment: (
    postId: number | string,
    commentId: number,
    reaction: ReactionType
  ) => Promise<boolean>;

  // Utilities
  getComments: (postId: number | string) => ApiComment[];
  clearPost: (postId: number | string) => void;
  reset: () => void;
};

const key = (postId: number | string) => String(postId);

/*
|--------------------------------------------------------------------------
| Store
|--------------------------------------------------------------------------
*/

export const useCommentStore = create<CommentStoreState>((set, get) => ({
  byPost: {},
  isLoadingByPost: {},
  isSubmittingByPost: {},
  errorByPost: {},
  hasMoreByPost: {},
  nextCursorByPost: {},

  // ==================== Load Comments ====================
  loadComments: async (postId, reset = false) => {
    const k = key(postId);

    if (get().isLoadingByPost[k]) return;

    const currentUserId =
      useAuthStore.getState().currentUser?.id ?? null;

    set((s) => ({
      isLoadingByPost: { ...s.isLoadingByPost, [k]: true },
      errorByPost: { ...s.errorByPost, [k]: "" },
      ...(reset
        ? {
            byPost: { ...s.byPost, [k]: [] },
            nextCursorByPost: { ...s.nextCursorByPost, [k]: null },
            hasMoreByPost: { ...s.hasMoreByPost, [k]: true },
          }
        : {}),
    }));

    try {
      const res = await commentApi.loadComments(
        postId,
        undefined,
        15,
        currentUserId
      );

      // ✅ عكس الترتيب عشان الأحدث يظهر الأول
      const reversed = reverseComments(res.data ?? []);

      set((s) => ({
        byPost: { ...s.byPost, [k]: reversed },
        nextCursorByPost: {
          ...s.nextCursorByPost,
          [k]: res.meta?.next_cursor ?? null,
        },
        hasMoreByPost: {
          ...s.hasMoreByPost,
          [k]: res.meta?.has_more ?? false,
        },
        isLoadingByPost: { ...s.isLoadingByPost, [k]: false },
      }));
    } catch (error) {
      set((s) => ({
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
        isLoadingByPost: { ...s.isLoadingByPost, [k]: false },
      }));
    }
  },

  // ==================== Load More ====================
  loadMore: async (postId) => {
    const k = key(postId);
    const { nextCursorByPost, hasMoreByPost, isLoadingByPost, byPost } =
      get();

    if (!hasMoreByPost[k] || isLoadingByPost[k] || !nextCursorByPost[k])
      return;

    const currentUserId =
      useAuthStore.getState().currentUser?.id ?? null;

    set((s) => ({
      isLoadingByPost: { ...s.isLoadingByPost, [k]: true },
    }));

    try {
      const res = await commentApi.loadComments(
        postId,
        nextCursorByPost[k],
        15,
        currentUserId
      );

      // ✅ عكس الجرعة الجديدة عشان تظهر الأحدث الأول
      const reversed = reverseComments(res.data ?? []);

      set((s) => ({
        byPost: {
          ...s.byPost,
          // ✅ الجرعة الجديدة تتحط في الأول
          [k]: [...reversed, ...(byPost[k] ?? [])],
        },
        nextCursorByPost: {
          ...s.nextCursorByPost,
          [k]: res.meta?.next_cursor ?? null,
        },
        hasMoreByPost: {
          ...s.hasMoreByPost,
          [k]: res.meta?.has_more ?? false,
        },
        isLoadingByPost: { ...s.isLoadingByPost, [k]: false },
      }));
    } catch (error) {
      set((s) => ({
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
        isLoadingByPost: { ...s.isLoadingByPost, [k]: false },
      }));
    }
  },

  // ==================== Add Comment (or reply) ====================
  addComment: async (postId, payload) => {
    const k = key(postId);
    const currentUserId =
      useAuthStore.getState().currentUser?.id ?? null;

    set((s) => ({
      isSubmittingByPost: { ...s.isSubmittingByPost, [k]: true },
      errorByPost: { ...s.errorByPost, [k]: "" },
    }));

    try {
      const created = await commentApi.createComment(
        postId,
        payload,
        currentUserId
      );

      set((s) => {
        const current = s.byPost[k] ?? [];

        // ✅ لو reply، ضيفه في الشجرة
        // لو top-level، ضيفه في الأول (الأحدث الأول)
        const next = payload.parentId
          ? addReplyToTree(current, payload.parentId, created)
          : [created, ...current];

        return {
          byPost: { ...s.byPost, [k]: next },
          isSubmittingByPost: { ...s.isSubmittingByPost, [k]: false },
        };
      });

      return true;
    } catch (error) {
      set((s) => ({
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
        isSubmittingByPost: { ...s.isSubmittingByPost, [k]: false },
      }));
      return false;
    }
  },

  // ==================== Edit Comment ====================
  editComment: async (postId, commentId, body) => {
    const k = key(postId);
    const currentUserId =
      useAuthStore.getState().currentUser?.id ?? null;

    set((s) => ({
      isSubmittingByPost: { ...s.isSubmittingByPost, [k]: true },
    }));

    try {
      const updated = await commentApi.updateComment(
        commentId,
        body,
        currentUserId
      );

      set((s) => ({
        byPost: {
          ...s.byPost,
          [k]: updateInTree(s.byPost[k] ?? [], commentId, {
            body: updated.body,
            updatedAt: updated.updatedAt,
            edited: true,
          }),
        },
        isSubmittingByPost: { ...s.isSubmittingByPost, [k]: false },
      }));

      return true;
    } catch (error) {
      set((s) => ({
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
        isSubmittingByPost: { ...s.isSubmittingByPost, [k]: false },
      }));
      return false;
    }
  },

  // ==================== Delete Comment ====================
  deleteComment: async (postId, commentId) => {
    const k = key(postId);

    // optimistic
    const previous = get().byPost[k] ?? [];
    set((s) => ({
      byPost: {
        ...s.byPost,
        [k]: removeFromTree(previous, commentId),
      },
    }));

    try {
      await commentApi.deleteComment(commentId);
      return true;
    } catch (error) {
      // rollback
      set((s) => ({
        byPost: { ...s.byPost, [k]: previous },
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
      }));
      return false;
    }
  },

  // ==================== React to Comment ====================
  reactToComment: async (postId, commentId, reaction) => {
    const k = key(postId);
    const currentUserId =
      useAuthStore.getState().currentUser?.id ?? null;

    // optimistic
    const previous = get().byPost[k] ?? [];
    set((s) => ({
      byPost: {
        ...s.byPost,
        [k]: updateInTree(previous, commentId, {
          myReaction: reaction,
        }),
      },
    }));

    try {
      const updated = await commentApi.reactToComment(
        commentId,
        reaction,
        currentUserId
      );

      set((s) => ({
        byPost: {
          ...s.byPost,
          [k]: updateInTree(s.byPost[k] ?? [], commentId, {
            myReaction: updated.myReaction ?? reaction,
            reactionsCount: updated.reactionsCount,
          }),
        },
      }));

      return true;
    } catch (error) {
      // rollback
      set((s) => ({
        byPost: { ...s.byPost, [k]: previous },
        errorByPost: { ...s.errorByPost, [k]: parseError(error) },
      }));
      return false;
    }
  },

  // ==================== Utilities ====================
  getComments: (postId) => get().byPost[key(postId)] ?? [],

  clearPost: (postId) => {
    const k = key(postId);
    set((s) => {
      const { [k]: _, ...restByPost } = s.byPost;
      const { [k]: __, ...restLoading } = s.isLoadingByPost;
      const { [k]: ___, ...restErr } = s.errorByPost;
      return {
        byPost: restByPost,
        isLoadingByPost: restLoading,
        errorByPost: restErr,
      };
    });
  },

  reset: () =>
    set({
      byPost: {},
      isLoadingByPost: {},
      isSubmittingByPost: {},
      errorByPost: {},
      hasMoreByPost: {},
      nextCursorByPost: {},
    }),
}));